import { Button, OldSwitch } from "@app/form_components";
import { BranchDialog } from "@app/widgets/dialogs/BranchDialog";
import BaseComponent from "../../../form_components/BaseComponent";
import Utility from "../../../utilities/Utility";
import LastPulledWidget from './LastPulledWidget';
import BranchMergeEventFactory from "../../../utilities/versioning/BranchMergeEventFactory";
import {
  addBreakingChange,
  createUuid,
  hasLocalChanges
} from "../../../utilities";
import { BreakingChangeType } from "../../../types";
import { MergeDialog, MergeDialogType } from "../../dialogs/MergeDialog";
import { defaultServiceContext } from "../../../contexts";

const {
    avsService,
    errorMessageService,
    loadingService,
    globalModalsService
} = defaultServiceContext;

export default class BranchingMergingUpgrade extends BaseComponent {
    /**
     * Global exception handling
     * @module BranchingMergingUpgrade
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);

        this.state = {
            branchDialogProps: { isHotfix: false },
            isBranchDialogOpen: false,
            isBranchingMergingInitialized: !!props.serialized.branchingMerging?.initialized
        };
        this.branchMergeEventFactory = new BranchMergeEventFactory();
    }

    componentDidMount(){
        this.updateUIState();
    }

    UNSAFE_componentWillReceiveProps(){
        this.updateUIState();    
    }
    
    /**
     * Manages the `isBranchingMergingInitialized` key on branchingMerging
     * @param {string} key 
     * @param {boolean} value 
     */
    setConfig(key, value) {
        
        const serialized = window.apm.serialized;
        const shouldInitializeBranchingMerging = !serialized.branchingMerging?.initialized;

        if(shouldInitializeBranchingMerging && value === true){
            Utility.openApplicationOverlay("Checking for unsaved changes ...");
            this.openBranchAndMergeWindow('upgrade',serialized);
        }
    }

    getConfig(key) {
        return window.apm.serialized.branchingMerging?.[key] ?? false;
    }

    /**
     * Control the UI (dropdowns) via state
     */
     updateUIState(){
        this.setState({isBranchingMergingInitialized: this.getConfig("initialized")});
    }

    /**
     * Modal to confirm Branching & Mergin upgrade.
     */
    confirmUpgrade(serialized) {
        w2confirm({
            msg: `<p>Upgrading to Branching & Merging requires an application save and is not backwards compatible. <b><u>This cannot be undone.</u></b> Do you wish to continue?</p>
                  <div style="display: flex; justify-content: center">
                    <input type="checkbox" onchange="document.getElementById('Yes').disabled = !document.getElementById('branchingMerging').checked" id="branchingMerging" name="branchingMerging" style="margin: 3px 4px;">
                    <label for="branchingMerging" style="font-weight: normal">Upgrade to Branching & Merging.</label>
                  </div>`,
            title: 'Upgrade to Branching & Merging',
            width: 453,
            height: 280,
            btn_yes: {
                text: 'Yes',
                style: '',
                class: 'primary',
                callBack: () =>  {
                    if(document.getElementById('branchingMerging').checked === true){
                        this.upgradeBranchingMerging(serialized);
                    } else {
                        document.getElementById('pleaseConfirm').style.display = 'block';
                    }
                }
                        
            },
            btn_no: {
                text: 'No',
                style: '',
                class: 'secondary',
                callBack: () => this.setState({isBranchingMergingInitialized: false})
            },
            callBack: null,     // common callBack
            onOpen: this.disableYes('#Yes').then((elm) => {
                elm.disabled = true;
            })
        });
    }

    disableYes(selector) {
        return new Promise(resolve => {
            if (document.querySelector(selector)) {
                return resolve(document.querySelector(selector));
            }
    
            const observer = new MutationObserver(mutations => {
                if (document.querySelector(selector)) {
                    resolve(document.querySelector(selector));
                    observer.disconnect();
                }
            });
    
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        });
    }

    upgradeBranchingMerging(serialized){
        let clonedSerialized = _.cloneDeep(serialized);

         // B&M requires that all data has id fields
        // B&M appProperties may not have ids
        clonedSerialized = this.checkAppPropertyIds(clonedSerialized, window.apm.timestamp);
        // B&M node and link model config items may not have ids
        clonedSerialized = this.checkModelConfigItemsIds(clonedSerialized, window.apm.timestamp);

        clonedSerialized.branchingMerging = {  
            initialized: true,
            lastLocalChange: window.apm.timestamp
        };

        addBreakingChange(clonedSerialized, {
            message: 'Branching and Merging enabled.',
            timestamp: window.apm.timestamp,
            type: BreakingChangeType.branchingEnabled
        });

        this.props.openSaveDialog(clonedSerialized);
    }

    async testForUnsavedChanges() {
        const { serialized } = window.apm;
        const { branchId, version } = serialized;
        const latestVersion = (
            await avsService.getLatestVersion(branchId)
        )?.version;
        if (latestVersion === null) {
            return { neverSaved: true };
        }

        const notOnLatestVersion = version !== latestVersion;
        const savedSerialized = await avsService.getDesign(branchId, version);
        const unsavedChanges = hasLocalChanges({
            design: serialized,
            latestDesign: savedSerialized
        });
        return {
            unsavedChanges,
            notOnLatestVersion
        };
    }

    /**
     * @param {"branch" | "merge" | "upgrade"} type
     */
    async openBranchAndMergeWindow(type, serialized = null) {
        let errorMessage = null;
        try {
            const results = await this.testForUnsavedChanges();
            if (results.neverSaved) {
                // If serialized is not saved, then type is always "upgrade".
                errorMessage = "Please save to repository before upgrading";
            } else if (results.notOnLatestVersion && type === "upgrade") {
                // "branch" & "merge" can be on old versions for hotfixes.
                errorMessage = "Upgrading is only available from latest version";
            } else if (results.unsavedChanges) {
                errorMessage = "Please save changes before continuing.";
            } else if (type === "branch" || type === "merge") {
                const isHotfix = results.notOnLatestVersion;
                Utility.closeApplicationOverlay();
                if (isHotfix && !await globalModalsService.openConfirmationModal({
                    children: `You are attempting to ${type} from an old version. Are you sure you want to make a hotfix version?`
                })) {
                    return;
                }

                if (type === "branch") {
                    this.setState({ isBranchDialogOpen: true, branchDialogProps: { isHotfix } });
                } else {
                    window.apm.openMergeBranchWindow(window.apm.serialized, isHotfix);
                }
            } else if (type === "upgrade") {
                this.confirmUpgrade(serialized);
            }
        } catch (error) {
            errorMessage = "Error checking for unsaved changes";
            errorMessageService.addAvsUnresponsiveError(error);
        }

        if (errorMessage) {
            window.apm.notification.post("error", errorMessage);
            if (type === "upgrade") {
                this.setState({ isBranchingMergingInitialized: false });
            }
        }
        Utility.closeApplicationOverlay();
    }

    /**
     * Adds id fields to appProperties if they do not have it
     * @param {object} serialized 
     * @param {number} timestamp 
     * @returns serialized
     */
     checkAppPropertyIds (serialized, timestamp) {

        serialized.appProperties.forEach(appProp => {
            if(!appProp.id){
                appProp.id = createUuid();
                appProp.lastLocalChange = timestamp
            }
        });
    
        return serialized;
    }

    /**
     * Adds id fields to node/link model config items if they do not have it
     * @param {object} serialized 
     * @param {number} timestamp 
     * @returns serialized
     */
    checkModelConfigItemsIds(serialized, timestamp){
        serialized.components.modules.forEach(mod => {
            if(mod.localVariables && mod.localVariables.length > 0){
                this.updateLocalVariablePropsConfigIdsupdateVariableId(mod.localVariables, mod.props, timestamp);
            }
            
            if(mod.props.config.inputs && mod.props.config.inputs.length > 0){
                this.updateVariableId(mod.props.config.inputs, mod.props, timestamp);
            }
            if(mod.props.config.outputs && mod.props.config.outputs.length > 0){
                this.updateVariableId(mod.props.config.outputs, mod.props, timestamp);
            }
    
            // TODO: replace with usedIn in teh following component.objects forEach
            const nodes = mod.versions[1].flow.nodes;
            nodes.forEach(node => {
                if(node.props.config.inputs && node.props.config.inputs.length > 0){
                    this.updateVariableId(node.props.config.inputs, node.props, timestamp);
                }
                if(node.props.config.outputs && node.props.config.outputs.length > 0){
                    this.updateVariableId(node.props.config.outputs, node.props, timestamp);
                }
                if(node.props.config.function && node.props.config.function.arguments && node.props.config.function.arguments.length > 0){
                    this.updateVariableId(node.props.config.function.arguments, node.props, timestamp);
                }
                if(node.props.config.function && node.props.config.function.outputs && node.props.config.function.outputs.length > 0){
                    this.updateVariableId(node.props.config.function.outputs, node.props, timestamp);
                }
                // inputTimeOut (single item array)
                // matchOrder
                // outputTo
                
            })
    
            const links = mod.versions[1].flow.links;
            links.forEach(link => {
                if(link.props.config.inputs && link.props.config.inputs.length > 0){
                    this.updateVariableId(link.props.config.inputs, link.props, timestamp);
                }
                if(link.props.config.outputs && link.props.config.outputs.length > 0){
                    this.updateVariableId(link.props.config.outputs, link.props, timestamp);
                }
            })
        });
    
        // TODO: usedIn for Objects instead of nodes in modules 
        serialized.components.objects.forEach(obj => {
            if(obj.props.config.inputs && obj.props.config.inputs.length > 0){
                this.updateVariableId(obj.props.config.inputs, obj.props, timestamp);    
            }
            if(obj.props.config.outputs && obj.props.config.outputs.length > 0){
                this.updateVariableId(obj.props.config.outputs, obj.props, timestamp);    
            }
    
            if(obj.props.config.function && obj.props.config.function.arguments && obj.props.config.function.arguments.length > 0){
                this.updateVariableId(obj.props.config.function.arguments, obj.props, timestamp);    
            }
    
            if(obj.props.config.function && obj.props.config.function.outputs && obj.props.config.function.outputs.length > 0){
                this.updateVariableId(obj.props.config.function.outputs, obj.props, timestamp);    
            }
        });
    
        // TODO: variable.props.usedIn depricated, but cannot remove yet
        serialized.components.variables.forEach(variable => {
            // delete variable.props.usedIn;
        })
        
        serialized.globalIntents.forEach(gi => {
            gi.moduleProperties.props.config.inputs = gi.moduleProperties.props.config.inputs || [];
            gi.moduleProperties.props.config.inputs.forEach(input => {
                if(!input.id){
                    input.id = createUuid();
                    input.lastLocalChange = window.apm.timestamp;
                }
            });
    
            gi.moduleProperties.props.config.outputs = gi.moduleProperties.props.config.outputs || [];
            gi.moduleProperties.props.config.outputs.forEach(output => {
                if(!output.id){
                    output.id = createUuid();
                    input.lastLocalChange = window.apm.timestamp;
                }
            });
        })

        serialized.functions ??= [];
        serialized.functions.forEach(func => {
            if(func.parameters){
                func.parameters.forEach(param => {
                    if(!param.id){
                        param.id = createUuid();
                        func.lastLocalChange = timestamp;
                    }
                })
            }
        });
       
        return serialized;
    }
    
    updateVariableId(collection, props, timestamp){
        collection.forEach(variable => {
            if(!variable.id){
                variable.id = createUuid();
                props.lastLocalChange = timestamp;
            } 
        });
    
        return collection;
    }
    
    updateLocalVariablePropsConfigIdsupdateVariableId(collection, props, timestamp){
        collection.forEach(lv => {
            if(!lv.props.config.id){
                lv.props.config.id = createUuid();
                props.lastLocalChange = timestamp;
            } 
        });
    
        return collection;
    }

    render() {
        const branchingMergingInitialized = this.state.isBranchingMergingInitialized;
        const isBranch = this.props.serialized.branching;
        const isMain = branchingMergingInitialized && !isBranch;
        const hasBranch = this.props.serialized.hasOwnProperty('branchingMap');

        return (
            <div>
                {!branchingMergingInitialized &&
                    <div className="row">
                        <div className="col-md-6">
                            <OldSwitch 
                                context={this} 
                                valKey="isBranchingMerging"
                                label="Branching & Merging"
                                checked={branchingMergingInitialized} 
                                tooltip="Upgrades application to use Branching &amp; Merging"
                                disabled={branchingMergingInitialized}
                            />
                            
                        </div>
                        <div className="col-md-6">
                            <div className="mergeWarning">
                                <span>Warning: Upgrade Cannot Be Undone</span>
                            </div>
                        </div>
                    </div>
                }

                {branchingMergingInitialized &&
                        <div>
                            { isMain &&   
                                <div style={{display: "flex", justifyContent: "space-between", gap: 'var(--space-small)'}}>
                                    <div>
                                        <Button 
                                            label="Create Branch" 
                                            labelIcon="git-branch"
                                            callback={() => {
                                                Utility.openApplicationOverlay("Checking for unsaved changes ...");
                                                this.openBranchAndMergeWindow('branch')
                                            }}
                                            disabled={this.props.isSaveNewVersionChecked}
                                        />
                                    </div>
                                    {hasBranch &&
                                        <div>
                                            <Button
                                                label="Merge Branch"
                                                labelIcon="git-merge"
                                                callback={() => {
                                                    Utility.openApplicationOverlay("Checking for unsaved changes ...");
                                                    this.openBranchAndMergeWindow('merge')
                                                }}
                                                disabled={this.props.isSaveNewVersionChecked}
                                            />
                                        </div>
                                    }
                                </div>
                            }

                            {isBranch &&    
                                <div style={{display: 'inline-block'}}>
                                    <Button 
                                        label="Pull from Main"
                                        labelIcon="git-merge"
                                        callback={async () => {
                                            const unsavedChangeLoadingId = loadingService.addMessage('Checking for unsaved changes ...');

                                            const {
                                                notOnLatestVersion,
                                                unsavedChanges
                                            } = await this.testForUnsavedChanges();
                                            loadingService.removeMessage(unsavedChangeLoadingId);
            
                                            if (notOnLatestVersion) {
                                                // must be on the latest version
                                                // TODO: replace alert 
                                                alert('Pulling is only available from latest version of this branch');
                                                return;
                                            } else if (unsavedChanges) {
                                                // must have no unsaved changes
                                                // TODO: replace alert 
                                                alert('Please save changes before pulling latest from main');
                                                return;
                                            }

                                            const pullLoadingId = loadingService.addMessage('Preparing pull for review...');
                                            const {
                                                sourceDesign,
                                                sourceOriginDesign,
                                                targetDesign,
                                                targetOriginDesign
                                            } = await avsService.getDocumentsForPull(window.apm.serialized);
                                            loadingService.removeMessage(pullLoadingId);

                                            MergeDialog.open({
                                                defaultCommitMessage: `Branch ${sourceDesign.application} pulled at version ${sourceDesign.version}`,
                                                sourceDesign,
                                                sourceOriginDesign,
                                                targetDesign,
                                                targetOriginDesign,
                                                type: MergeDialogType.pull
                                            });
                                        }}
                                    />
                                    
                                    {/* Display how many version behind main, or 'already up to date' */}
                                    <div className="pull_status">
                                        <LastPulledWidget 
                                            serialized={this.props.serialized}
                                        />
                                    </div>
                                    
                                </div>
                            }

                        </div>
                }
                <BranchDialog
                    {...this.state.branchDialogProps}
                    closeAction={() => this.setState({ isBranchDialogOpen: false })}
                    isOpen={this.state.isBranchDialogOpen}
                />
            </div>
        );
    }
}